import styled from 'styled-components'
import { createPortal, unmountComponentAtNode } from 'react-dom'
import { useRef, useState, useLayoutEffect, useEffect, useMemo, useContext } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { Router, useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'

import { api } from '@h2/api'
import { Globals } from '@h2/globals'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { features } from '@h2/components/media'
import { PG, PB, SDB, SLB, PB97T, WHITE } from '@h2/components/colors'

import { EmailConfirmationWarning } from './emailConfirmationWarning'
import { SignInOrRegisterDialog } from './signInOrRegisterDialog'
import { CallToAction } from './callToAction'
import { Navigation } from './navigation'
import { ViewSwitcher } from './viewSwitcher'
import { ViewSwitcherProfile } from './viewSwitcherProfile'
import { Branding } from './branding'
import { Search } from './search'
import { Context } from './context'

import { Panel } from '../panel'
import { useContextProvider } from '../../components/hooks/useContextProvider'
import { VIEW_MODE_MANAGEMENT, LESSON_UPDATED_EVENT } from '../../constants'
import { isDialogRegistrationActive } from '../../selectors/isDialogRegistrationActive'

const PanelNav = styled(Panel)`
  background-color: ${({ isLoggedIn, viewMode, tenantId, isEarlyLearnerMode }) =>
    isEarlyLearnerMode
      ? SLB
      : isLoggedIn
      ? viewMode === VIEW_MODE_MANAGEMENT
        ? SDB
        : tenantId === 'california'
        ? '#225FDE'
        : PG
      : PB};

  > div {
    margin: 0;
    padding: 0;
  }

  @media (${features.minWidth.laptop}) {
    position: fixed;
    z-index: 10000;
    width: 100%;
    > div {
      margin: 0 auto;
      padding: 0 15px;

      > div {
        justify-content: ${({ isLoggedIn }) =>
          isLoggedIn ? 'flex-end' : 'space-between'};
        align-items: center;
      }
    }
  }
`
const BoxNav = styled(Box)`
  @media (${features.minWidth.laptop}) {
    /* Allow active lessons to grow or shrink. */
    flex-grow: 1;
  }
`
const PanelBrand = styled(Panel)`
  background-color: ${({ isNavianceUser }) => (isNavianceUser ? PB97T : WHITE)};
  padding-top: ${({ isLoggedIn, switcherProfileHeight }) =>
    isLoggedIn ? switcherProfileHeight : 0};

  > div > div {
    padding: 20px 0;
  }

  @media (${features.minWidth.laptop}) {
    padding-top: ${({ navHeight }) => navHeight};

    > div > div {
      justify-content: space-between;
    }
  }
`
const FlexBrand = styled(Flex)`
  > div:last-child {
    width: 100%;
  }

  @media (${features.minWidth.laptop}) {
    width: 100%;
  }
`
const headerElement = document.getElementById('h2-header')
const Header = ({ isSpaChild = false }) => {
  const browserHistory = useHistory()
  const { pathname: currentLocation } = useLocation()
  const shouldSuppress =
    currentLocation.startsWith('/early') || currentLocation.startsWith('/cms')
  const queryClient = useQueryClient()
  const isDialogRegActive = useSelector(isDialogRegistrationActive)
  const globalCtx = useContext(Globals)
  const {
    tenantId,
    isLoggedIn,
    isNavianceUser,
    viewMode,
    handleOnViewChange,
    isEarlyLearnerMode,
    user,
    isMobile,
    requireAuthentication,
    navigationRef: navRef,
    ...rest
  } = useContextProvider({ ...dynamicValues, ...globalCtx })
  // List of URL's in Account Linking Flow
  const showNavBarAccountLinkFlow = [
    '/accept',
    '/signin',
    '/register',
    '/naviancelinkaccount/signin',
    '/naviancelinkaccount/register',
    '/resetpassword'
  ]
  const renderNavBarLinkFlow =
    isNavianceUser && showNavBarAccountLinkFlow.includes(currentLocation)
  const shouldRenderNav = !renderNavBarLinkFlow && (isLoggedIn || !requireAuthentication)
  const viewSwitcherProfileRef = useRef(null)
  const [navHeight, setNavHeight] = useState(() => (shouldRenderNav ? '52px' : 0))

  useEffect(() => {
    setNavHeight(shouldRenderNav ? '52px' : 0)
  }, [shouldRenderNav])
  const [switcherProfileHeight, setSwitcherProfileHeight] = useState(
    isMobile && isLoggedIn ? '38px' : 0
  )

  useEffect(() => {
    setSwitcherProfileHeight(isMobile && isLoggedIn ? '38px' : 0)
  }, [isMobile, isLoggedIn])

  const { data: userMenu, isFetching: isMenuFetching } = useQuery(
    ['userMenu', user],
    () => api.fetch('/user/menu')
  )
  const context = useMemo(
    () => ({
      ...rest,
      tenantId,
      userMenu,
      navHeight,
      viewMode,
      switcherProfileHeight,
      handleOnViewChange,
      requireAuthentication,
      isLoggedIn,
      isMenuFetching,
      isMobile
    }),
    [
      rest,
      tenantId,
      navHeight,
      userMenu,
      viewMode,
      switcherProfileHeight,
      handleOnViewChange,
      requireAuthentication,
      isLoggedIn,
      isMenuFetching,
      isMobile
    ]
  )
  const render = () => (
    <Context.Provider value={context}>
      <Router history={browserHistory}>
        {shouldRenderNav && (
          <PanelNav
            isLoggedIn={isLoggedIn}
            viewMode={viewMode}
            tenantId={tenantId}
            isEarlyLearnerMode={isEarlyLearnerMode}>
            <Flex>
              {!isLoggedIn && !isMobile && (
                <Box>
                  <CallToAction />
                </Box>
              )}
              <BoxNav>
                <Navigation ref={navRef} />
              </BoxNav>
              {isMobile && isLoggedIn && (
                <Box>
                  <ViewSwitcherProfile ref={viewSwitcherProfileRef} />
                </Box>
              )}
            </Flex>
          </PanelNav>
        )}
        <PanelBrand
          isNavianceUser={isNavianceUser}
          isLoggedIn={isLoggedIn}
          navHeight={navHeight}
          switcherProfileHeight={switcherProfileHeight}>
          <Flex alignItems="center" justifyContent="center">
            <FlexBrand alignItems="center" justifyContent="space-between">
              <Box>
                <Branding renderBranding={renderNavBarLinkFlow} />
              </Box>
              {!isEarlyLearnerMode && (
                <Box>
                  <ViewSwitcher />
                </Box>
              )}
            </FlexBrand>
            <Box>
              <Search renderSearch={renderNavBarLinkFlow} />
            </Box>
          </Flex>
        </PanelBrand>
        {isLoggedIn && (
          <Panel>
            <EmailConfirmationWarning />
          </Panel>
        )}
        {(!isLoggedIn || isDialogRegActive) && (
          <SignInOrRegisterDialog isDialogRegActive={isDialogRegActive} />
        )}
      </Router>
    </Context.Provider>
  )

  useLayoutEffect(() => {
    if (navRef.current) {
      setNavHeight(window.getComputedStyle(navRef.current).height)
    }

    if (viewSwitcherProfileRef.current) {
      setSwitcherProfileHeight(
        window.getComputedStyle(viewSwitcherProfileRef.current).height
      )
    }
  }, [isMobile, isLoggedIn, navRef])

  useEffect(() => {
    const handleLessonUpdated = async () => {
      await queryClient.refetchQueries('userMenu', { refetchType: 'all' })
    }

    window.addEventListener(LESSON_UPDATED_EVENT, handleLessonUpdated)

    return () => window.removeEventListener(LESSON_UPDATED_EVENT, handleLessonUpdated)
  }, [queryClient])

  useEffect(() => {
    if (headerElement && isSpaChild && !headerElement.dataset.portal) {
      // This dataset attribute is here so HMR works as expected
      headerElement.dataset.portal = 'yes'
      unmountComponentAtNode(headerElement)
    }
  }, [isSpaChild])

  if (globalCtx && headerElement) {
    return createPortal(render(), headerElement)
  }

  return shouldSuppress ? null : render()
}

Header.propTypes = {
  /**
   * Whether the component is rendered as a child of the
   * App's DOM element (id === spatarget) in the react tree.
   */
  isSpaChild: PropTypes.bool
}

export { Header }
